import { Injectable, signal } from '@angular/core';

export type PromptInputType = 'text' | 'number' | 'textarea';

export interface PromptRequest {
  title: string;
  message?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
  inputType?: PromptInputType;
  required?: boolean;
  min?: number;
  max?: number;
  step?: number;
}

interface PromptState {
  open: boolean;
  req: PromptRequest | null;
  value: string;
  error: string | null;
}

@Injectable({ providedIn: 'root' })
export class PromptDialogService {
  state = signal<PromptState>({ open: false, req: null, value: '', error: null });
  private resolver: ((value: string | null) => void) | null = null;

  prompt(req: PromptRequest): Promise<string | null> {
    if (this.resolver) {
      this.resolver(null);
      this.resolver = null;
    }
    return new Promise<string | null>((resolve) => {
      this.resolver = resolve;
      this.state.set({
        open: true,
        req,
        value: req.defaultValue ?? '',
        error: null,
      });
    });
  }

  setValue(value: string) {
    this.state.update((s) => ({ ...s, value, error: null }));
  }

  confirm() {
    const s = this.state();
    if (!s.req) return;
    const value = s.value ?? '';

    if (s.req.required && !value.trim()) {
      this.state.update((cur) => ({ ...cur, error: 'Este campo es obligatorio.' }));
      return;
    }

    if (s.req.inputType === 'number') {
      const num = Number(value);
      if (Number.isNaN(num)) {
        this.state.update((cur) => ({ ...cur, error: 'Ingresa un número válido.' }));
        return;
      }
      if (s.req.min !== undefined && num < s.req.min) {
        this.state.update((cur) => ({ ...cur, error: `Mínimo ${s.req!.min}.` }));
        return;
      }
      if (s.req.max !== undefined && num > s.req.max) {
        this.state.update((cur) => ({ ...cur, error: `Máximo ${s.req!.max}.` }));
        return;
      }
    }

    this.state.set({ open: false, req: null, value: '', error: null });
    const r = this.resolver;
    this.resolver = null;
    r?.(value);
  }

  cancel() {
    this.state.set({ open: false, req: null, value: '', error: null });
    const r = this.resolver;
    this.resolver = null;
    r?.(null);
  }
}
